import type { Message } from '../../../i18n/types';

/** «Выкатки»: what a backend change on stage reaches — autotests, cases, what to check by hand. */
export const automationDeploysEn = {
  'automation.deploys.open': 'Deploys',
  'automation.deploys.title': 'Deploys to stage',
  'automation.deploys.hint':
    'The QA agent compares each backend service with its last check and links the change to autotests. TMS runs those autotests as soon as the deploy pipeline is green, and shows the cases they cover and the ones next to them to check by hand.',
  'automation.deploys.empty': 'No deploys yet',
  'automation.deploys.emptyHint':
    'They appear after the QA agent checks the backend («проверь бэк») with the agent token set — «Подключение» → «Агент QA».',
  'automation.deploys.state.waiting': 'Waiting for the deploy',
  'automation.deploys.state.started': 'Autotests started',
  'automation.deploys.state.noTests': 'No autotests found',
  'automation.deploys.state.notStarted': 'Not run',
  'automation.deploys.deploy': 'deploy: {status}',
  'automation.deploys.deployUnknown': 'deploy: unknown',
  'automation.deploys.commits': {
    one: '{count} commit',
    other: '{count} commits',
  },
  'automation.deploys.findings': 'What the agent noticed',
  'automation.deploys.classes': 'Autotests the change reaches',
  'automation.deploys.possible': 'Maybe reached — not run',
  'automation.deploys.manual': 'Next to them, no autotest — check by hand',
  'automation.deploys.unresolved': 'Not in TMS: {list}',
  'automation.deploys.noTests':
    'The agent linked no autotest to this change. Projects covering the service: {list}.',
  'automation.deploys.noCases': 'no linked cases',
  'automation.deploys.run': 'Run reached autotests',
  'automation.deploys.runStarted': 'Autotests started',
  'automation.deploys.runFailed': 'Autotests did not start — the reason is on the card',
  'automation.deploys.runError': '{project}: did not start — {error}',
  'automation.deploys.noTestsNoCover':
    'The agent linked no autotest to this change, and no automation project is set to cover the service.',
  'automation.deploys.startedBy': 'started by {who}',
  'automation.deploys.status.pending': 'running',
  'automation.deploys.status.none': 'not run',
  'automation.deploys.confidence.high': 'directly',
  'automation.deploys.confidence.medium': 'through shared code',
  'automation.deploys.confidence.low': 'maybe',
  'automation.deploys.async': 'asynchronous',
  'automation.deploys.asyncHint':
    'The change is in a temporal workflow the method only starts: the method answers before the changed code runs, so the test may not see it.',
  'automation.deploys.setupOnly': 'in the class preparation',
  'automation.deploys.untested': 'Reached, but no autotest calls it',
  'automation.deploys.untestedHint':
    'Methods the change reaches that no autotest calls by gRPC — check by hand. Tests through api-gateway count only when its route is named after the method.',
  'automation.deploys.methodsByFile': {
    one: '{count} method the change reaches',
    other: '{count} methods the change reaches',
  },
  'automation.deploys.methodsByFileHint':
    'No autotest calls them by gRPC: the service is tested through api-gateway, so the autotests above were found by file, as before.',
  'automation.deploys.notApi': 'Not through the API — check otherwise',
  'automation.deploys.quiet': 'No behaviour changed:',
  'automation.deploys.note.migration': 'database migration',
  'automation.deploys.note.kafka': 'Kafka consumer',
  'automation.deploys.note.job': 'background job',
  'automation.deploys.note.temporal': 'temporal workflow not started from the API',
  'automation.deploys.note.server': 'service start-up and background work',
  'automation.deploys.note.texts': 'texts',
  'automation.deploys.note.config': 'configuration',
  'automation.deploys.note.deps': 'dependencies',
  'automation.deploys.note.removed': 'removed',
  'automation.deploys.note.unreached': 'no caller found',
  'automation.deploys.note.other': 'other files',
  'automation.deploys.note.logging': 'logging only',
  'automation.deploys.note.comments': 'comments only',
  'automation.deploys.note.empty': 'merges with no changes',
  // agent token in «Подключение»
  'automation.connection.agent': 'QA agent',
  'automation.connection.agentHint':
    'The agent sends what changed on stage and which autotests it reaches. It signs in with this token: put it into TMS_AGENT_TOKEN where the agent runs.',
  'automation.connection.agentSet': 'set by {who}, {when}',
  'automation.connection.agentCreate': 'Create token',
  'automation.connection.agentReplace': 'Replace token',
  'automation.connection.agentRemove': 'Remove token',
  'automation.connection.agentShownOnce': 'Shown once — copy it now. TMS keeps only its hash.',
  'automation.connection.agentRemoved': 'Agent token removed',
} satisfies Record<string, Message>;

export const automationDeploysRu: Record<keyof typeof automationDeploysEn, Message> = {
  'automation.deploys.open': 'Выкатки',
  'automation.deploys.title': 'Выкатки на stage',
  'automation.deploys.hint':
    'Агент QA сравнивает каждый сервис бэка с прошлой проверкой и связывает изменение с автотестами. TMS запускает эти автотесты, как только деплой зелёный, и показывает кейсы, которые они покрывают, и соседние — пройти руками.',
  'automation.deploys.empty': 'Выкаток пока нет',
  'automation.deploys.emptyHint':
    'Появятся после проверки бэка агентом («проверь бэк»), когда задан токен агента — «Подключение» → «Агент QA».',
  'automation.deploys.state.waiting': 'Ждём деплой',
  'automation.deploys.state.started': 'Автотесты запущены',
  'automation.deploys.state.noTests': 'Автотестов не нашлось',
  'automation.deploys.state.notStarted': 'Не запускались',
  'automation.deploys.deploy': 'деплой: {status}',
  'automation.deploys.deployUnknown': 'деплой: неизвестно',
  'automation.deploys.commits': {
    one: '{count} коммит',
    few: '{count} коммита',
    many: '{count} коммитов',
    other: '{count} коммита',
  },
  'automation.deploys.findings': 'Что заметил агент',
  'automation.deploys.classes': 'Автотесты, которые задевает изменение',
  'automation.deploys.possible': 'Возможно задеты — не запускаются',
  'automation.deploys.manual': 'Рядом, без автотеста — пройти руками',
  'automation.deploys.unresolved': 'Нет в TMS: {list}',
  'automation.deploys.noTests':
    'Агент не связал изменение ни с одним автотестом. Сервис покрывают проекты: {list}.',
  'automation.deploys.noCases': 'кейсы не связаны',
  'automation.deploys.run': 'Прогнать задетые',
  'automation.deploys.runStarted': 'Автотесты запущены',
  'automation.deploys.runFailed': 'Автотесты не запустились — причина в карточке',
  'automation.deploys.runError': '{project}: не запустился — {error}',
  'automation.deploys.noTestsNoCover':
    'Агент не связал изменение ни с одним автотестом, и за сервисом не закреплён ни один проект автотестов.',
  'automation.deploys.startedBy': 'запустил {who}',
  'automation.deploys.status.pending': 'идёт',
  'automation.deploys.status.none': 'не запускался',
  'automation.deploys.confidence.high': 'напрямую',
  'automation.deploys.confidence.medium': 'через общий код',
  'automation.deploys.confidence.low': 'возможно',
  'automation.deploys.async': 'асинхронно',
  'automation.deploys.asyncHint':
    'Изменение в процессе temporal, который метод только запускает: метод отвечает раньше, чем выполнится изменённый код, и тест может его не увидеть.',
  'automation.deploys.setupOnly': 'в подготовке класса',
  'automation.deploys.untested': 'Задеты, но ни один автотест их не вызывает',
  'automation.deploys.untestedHint':
    'Методы, до которых дошло изменение и которые ни один автотест не вызывает по gRPC, — пройти руками. Тесты через api-gateway засчитываются, только когда ручка названа по методу.',
  'automation.deploys.methodsByFile': {
    one: '{count} задетый метод',
    few: '{count} задетых метода',
    many: '{count} задетых методов',
    other: '{count} задетого метода',
  },
  'automation.deploys.methodsByFileHint':
    'По gRPC их не вызывает ни один автотест: сервис тестируют через api-gateway, поэтому автотесты выше найдены по файлу, как раньше.',
  'automation.deploys.notApi': 'Не через API — проверить иначе',
  'automation.deploys.quiet': 'Без изменения поведения:',
  'automation.deploys.note.migration': 'миграция БД',
  'automation.deploys.note.kafka': 'консьюмер Kafka',
  'automation.deploys.note.job': 'фоновая задача',
  'automation.deploys.note.temporal': 'процесс temporal без запуска из API',
  'automation.deploys.note.server': 'запуск сервиса и фоновая работа',
  'automation.deploys.note.texts': 'тексты',
  'automation.deploys.note.config': 'конфигурация',
  'automation.deploys.note.deps': 'зависимости',
  'automation.deploys.note.removed': 'удалено',
  'automation.deploys.note.unreached': 'вызов не найден',
  'automation.deploys.note.other': 'прочие файлы',
  'automation.deploys.note.logging': 'только логирование',
  'automation.deploys.note.comments': 'только комментарии',
  'automation.deploys.note.empty': 'слияния без изменений',
  'automation.connection.agent': 'Агент QA',
  'automation.connection.agentHint':
    'Агент присылает, что изменилось на stage и какие автотесты это задевает. Входит этим токеном: положите его в TMS_AGENT_TOKEN там, где работает агент.',
  'automation.connection.agentSet': 'создал {who}, {when}',
  'automation.connection.agentCreate': 'Создать токен',
  'automation.connection.agentReplace': 'Заменить токен',
  'automation.connection.agentRemove': 'Удалить токен',
  'automation.connection.agentShownOnce':
    'Показывается один раз — скопируйте сейчас. TMS хранит только его хеш.',
  'automation.connection.agentRemoved': 'Токен агента удалён',
};
